import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Avatar,
  alpha,
  useMediaQuery,
  useTheme,
  Stack,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  Tabs,
  Tab,
  Badge,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  Comment as CommentIcon,
  Print as PrintIcon,
  Receipt as ReceiptIcon,
  Inventory as InventoryIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibilityIcon,
  Verified as VerifiedIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  
  AddComment as AddCommentIcon,
  History as HistoryIcon,
  VerifiedUser as VerifiedUserIcon,
  AccountBalance as AccountBalanceIcon,
  LocalShipping as LocalShippingIcon,
  Assignment as AuditIcon,
} from '@mui/icons-material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineDot,
  TimelineConnector,
  TimelineContent,
  TimelineOppositeContent,
} from '@mui/lab';
import Sidebar from '../../components/sidebars/Sidebar_auditor';
import TopNav from '../../components/top_bars/TopNav_auditor';

// ==================== CONSTANTS ====================
const REQUEST_STATUSES = {
  pending: { label: 'Pending', color: '#ff9800', icon: PendingIcon },
  account_approved: { label: 'Account Approved', color: '#2196f3', icon: CheckCircleIcon },
  gm_approved: { label: 'GM Approved', color: '#4caf50', icon: VerifiedIcon },
  partially_received: { label: 'Partially Received', color: '#ff9800', icon: LocalShippingIcon },
  completed: { label: 'Completed', color: '#4caf50', icon: CheckCircleIcon },
  rejected: { label: 'Rejected', color: '#f44336', icon: CancelIcon },
};

const DEPARTMENT = 'Audit Department';

// ==================== HELPER FUNCTIONS ====================

const sanitizeString = (str) => {
  if (!str || typeof str !== 'string') return '';
  return str.trim()
    .replace(/[<>]/g, '')
    .replace(/&[a-z]+;/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/on\w+=/gi, '')
    .replace(/[\uD800-\uDFFF]/g, '');
};

const sanitizeNumber = (num) => {
  if (num === null || num === undefined || num === '') return 0;
  const parsed = parseFloat(num);
  return isNaN(parsed) ? 0 : parsed;
};

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '₦0.00';
  const num = parseFloat(amount);
  if (isNaN(num)) return '₦0.00';
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return 'N/A';
  }
};

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return 'N/A';
  }
};

const logSecurityEvent = (eventType, details) => {
  console.debug(`[SECURITY-AUDITOR] ${eventType}:`, {
    timestamp: new Date().toISOString(),
    eventType,
    details,
    userAgent: navigator.userAgent,
  });
};

const validateAuditorUser = (userType) => {
  const validAuditorTypes = ['auditor', 'admin', 'audit'];
  return validAuditorTypes.includes(userType?.toLowerCase());
};

// ==================== MAIN COMPONENT ====================
const AuditorRemark = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState(0);

  // Data states
  const [purchaseRequests, setPurchaseRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingId, setProcessingId] = useState(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [hasRemarksFilter, setHasRemarksFilter] = useState('all');

  // Dialog states
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [remarkDialogOpen, setRemarkDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Form states
  const [auditorRemark, setAuditorRemark] = useState('');

  // User info
  const [userProfile, setUserProfile] = useState({
    phone: '',
    name: '',
    department: DEPARTMENT,
    user_type: '',
  });

  const toggleSidebar = () => setSidebarOpen(prev => !prev);

  // ==================== API CALLS ====================
  const getApiBaseUrl = () => process.env.REACT_APP_API_BASE_URL || '';

  const fetchUserProfile = useCallback(() => {
    try {
      const phoneNumber = localStorage.getItem('phoneNumber');
      const userName = localStorage.getItem('userName');
      const userType = localStorage.getItem('role');
      
      console.debug('[AuditorRemark] Loading user profile:', {
        phone: phoneNumber,
        name: userName,
        user_type: userType,
      });
      
      if (!validateAuditorUser(userType)) {
        console.warn('[AuditorRemark] User does not have auditor role:', userType);
        setSnackbar({ 
          open: true, 
          message: 'You do not have permission to access this page. Auditor role required.', 
          severity: 'error' 
        });
      }
      
      setUserProfile({
        phone: phoneNumber || '',
        name: userName || '',
        department: DEPARTMENT,
        user_type: userType || '',
      });
    } catch (err) {
      console.error('[AuditorRemark] Error loading user profile:', err);
    }
  }, []);

  const fetchPurchaseRequests = useCallback(async () => {
    const startTime = performance.now();
    console.debug('[AuditorRemark] Fetching purchase requests for audit review...');

    try {
      setLoading(true);
      const baseUrl = getApiBaseUrl();
      
      const response = await axios.get(`${baseUrl}/purchase_requests.php`, {
        timeout: 30000,
        headers: { 'Accept': 'application/json' }
      });
      
      console.debug('[AuditorRemark] API Response:', response.data);

      if (response.data.success) {
        const requests = (response.data.data || []).map(req => ({
          ...req,
          items: req.items || [],
          total_items: req.items?.length || 0,
          total_amount: parseFloat(req.total_amount) || 0,
          auditor_remark: req.auditor_remark || [],
          has_auditor_remark: (req.auditor_remark && req.auditor_remark.length > 0),
        }));
        
        setPurchaseRequests(requests);
        setStats(response.data.stats);
        
        console.debug(`[AuditorRemark] Loaded ${requests.length} requests in ${(performance.now() - startTime).toFixed(2)}ms`);
      } else {
        setError(response.data.message);
        setSnackbar({ open: true, message: response.data.message, severity: 'error' });
      }
    } catch (err) {
      console.error('[AuditorRemark] Fetch error:', err);
      let errorMessage = 'Failed to load purchase requests';
      if (err.code === 'ECONNABORTED') {
        errorMessage = 'Request timeout. Please check your network connection.';
      } else if (err.response) {
        errorMessage = err.response.data?.message || `Server error: ${err.response.status}`;
      }
      setError(errorMessage);
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  // ==================== AUDITOR REMARK HANDLER ====================
  const handleAddRemark = async () => {
    if (!selectedRequest) {
      console.error('[AuditorRemark] No request selected');
      setSnackbar({ open: true, message: 'Error: No request selected', severity: 'error' });
      return;
    }

    const trimmedRemark = auditorRemark?.trim();
    if (!trimmedRemark) {
      setSnackbar({ open: true, message: 'Please enter a remark', severity: 'error' });
      return;
    }

    if (trimmedRemark.length < 3) {
      setSnackbar({ open: true, message: 'Remark must be at least 3 characters', severity: 'error' });
      return;
    }

    logSecurityEvent('AUDITOR_REMARK_ATTEMPT', {
      request_id: selectedRequest.id,
      po_number: selectedRequest.po_number,
      remark_length: trimmedRemark.length,
    });

    setProcessingId(selectedRequest.id);
    setLoading(true);

    try {
      const baseUrl = getApiBaseUrl();
      const requestPayload = {
        id: selectedRequest.id,
        action: 'add_auditor_remark',
        remark: trimmedRemark,
        staff_name: userProfile.name || 'Audit Department',
      };

      console.debug('[AuditorRemark] Sending remark:', requestPayload);

      const response = await axios.put(`${baseUrl}/purchase_requests.php`, requestPayload, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        timeout: 30000,
      });

      console.debug('[AuditorRemark] Remark response:', response.data);

      if (response.data && response.data.success) {
        setSnackbar({ 
          open: true, 
          message: `✓ Auditor remark added to PO #${selectedRequest.po_number}`, 
          severity: 'success' 
        });
        setRemarkDialogOpen(false);
        setAuditorRemark('');
        setViewDialogOpen(false);
        await fetchPurchaseRequests();
        
        logSecurityEvent('AUDITOR_REMARK_SUCCESS', { 
          request_id: selectedRequest.id, 
          po_number: selectedRequest.po_number 
        });
      } else {
        const errorMsg = response.data?.message || 'Failed to add remark';
        setSnackbar({ open: true, message: errorMsg, severity: 'error' });
        logSecurityEvent('AUDITOR_REMARK_FAILED', { 
          request_id: selectedRequest.id, 
          error: errorMsg 
        });
      }
    } catch (err) {
      console.error('[AuditorRemark] Error adding remark:', err);
      
      let errorMessage = 'Failed to add remark';
      if (err.code === 'ECONNABORTED') {
        errorMessage = 'Request timeout. Please try again.';
      } else if (err.response) {
        errorMessage = err.response.data?.message || `Server error: ${err.response.status}`;
      }
      
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
      logSecurityEvent('AUDITOR_REMARK_ERROR', { 
        request_id: selectedRequest.id, 
        error: err.message 
      });
    } finally {
      setProcessingId(null);
      setLoading(false);
    }
  };

  // ==================== PRINT PURCHASE ORDER ====================
  const handlePrintPO = useCallback(() => {
    if (!selectedRequest) return;

    logSecurityEvent('AUDITOR_PRINT_ATTEMPT', {
      request_id: selectedRequest.id,
      po_number: selectedRequest.po_number,
    });

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      setSnackbar({ 
        open: true, 
        message: 'Please allow pop-ups to print', 
        severity: 'error' 
      });
      return;
    }

    const items = selectedRequest.items || [];
    const totalAmount = selectedRequest.total_amount || 0;
    const poNumber = selectedRequest.po_number || 'N/A';
    const requesterName = selectedRequest.requested_by_name || 'N/A';
    const department = selectedRequest.department || 'Store Keeping';
    const auditorRemarks = selectedRequest.auditor_remark || [];

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Audit Report - ${poNumber}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: 'Times New Roman', Times, serif;
            margin: 0;
            padding: 20px;
            background: white;
          }
          .container {
            max-width: 1000px;
            margin: 0 auto;
            border: 1px solid #ddd;
            padding: 30px;
          }
          .header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 3px solid #9C27B0;
            padding-bottom: 15px;
          }
          .header h1 {
            margin: 0;
            color: #9C27B0;
            font-size: 28px;
          }
          .audit-badge {
            background: #9C27B0;
            color: white;
            padding: 5px 15px;
            border-radius: 20px;
            display: inline-block;
            margin-top: 10px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin: 20px 0;
          }
          th, td {
            border: 1px solid #ddd;
            padding: 12px;
            text-align: left;
          }
          th {
            background-color: #f5f5f5;
          }
          .remarks-section {
            margin: 20px 0;
            padding: 15px;
            background: #f3e5f5;
            border-left: 4px solid #9C27B0;
          }
          .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #ddd;
            text-align: center;
            font-size: 11px;
          }
          @media print {
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="no-print" style="text-align: right; margin-bottom: 15px;">
            <button onclick="window.print();" style="padding: 8px 20px; background: #9C27B0; color: white; border: none; border-radius: 5px; cursor: pointer;">
              🖨️ Print Audit Report
            </button>
          </div>
          
          <div class="header">
            <h1>AUDIT OBSERVATION REPORT</h1>
            <div class="audit-badge">Purchase Order Audit Trail</div>
          </div>
          
          <div style="margin-bottom: 20px;">
            <p><strong>PO Number:</strong> ${poNumber}</p>
            <p><strong>Requester:</strong> ${sanitizeString(requesterName)}</p>
            <p><strong>Department:</strong> ${sanitizeString(department)}</p>
            <p><strong>Total Amount:</strong> ${formatCurrency(totalAmount)}</p>
            <p><strong>Date:</strong> ${formatDateTime(new Date())}</p>
          </div>

          <h3>Order Items</h3>
          <table>
            <thead>
              <tr><th>S/N</th><th>Item</th><th>Qty</th><th>Unit Price</th><th>Total</th></tr>
            </thead>
            <tbody>
              ${items.map((item, idx) => `
                <tr>
                  <td>${idx + 1}</td>
                  <td>${sanitizeString(item.item_name)}</td>
                  <td align="right">${sanitizeNumber(item.quantity)}</td>
                  <td align="right">${formatCurrency(item.unit_price)}</td>
                  <td align="right">${formatCurrency(item.total_price)}</td>
                </tr>
              `).join('')}
              <tr style="font-weight: bold; background: #f5f5f5;">
                <td colspan="4" align="right">GRAND TOTAL:</td>
                <td align="right">${formatCurrency(totalAmount)}</td>
              </tr>
            </tbody>
          </table>

          <div class="remarks-section">
            <h3>📋 Auditor Remarks & Observations</h3>
            ${auditorRemarks.length > 0 ? auditorRemarks.map(remark => `
              <div style="margin-bottom: 15px; padding: 10px; background: white; border-radius: 5px;">
                <p><strong>${sanitizeString(remark.added_by)}</strong> <small>(${formatDateTime(remark.added_at)})</small></p>
                <p>${sanitizeString(remark.remark)}</p>
              </div>
            `).join('') : '<p><em>No auditor remarks recorded for this purchase order.</em></p>'}
          </div>

          <div class="footer">
            <p>This is an automated audit report generated by Elegantè HR System</p>
            <p>Generated by: ${userProfile.name} on ${new Date().toLocaleString()}</p>
          </div>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
  }, [selectedRequest, userProfile.name]);

  // ==================== HELPER FUNCTIONS FOR UI ====================
  const getStatusChip = (status) => {
    const config = REQUEST_STATUSES[status] || { label: status, color: '#757575', icon: PendingIcon };
    return (
      <Chip
        label={config.label}
        size="small"
        icon={<config.icon sx={{ fontSize: 16 }} />}
        sx={{ bgcolor: alpha(config.color, 0.1), color: config.color, fontWeight: 'medium' }}
      />
    );
  };

  // Apply filters
  useEffect(() => {
    let filtered = [...purchaseRequests];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(req =>
        req.po_number?.toLowerCase().includes(term) ||
        req.requested_by_name?.toLowerCase().includes(term) ||
        req.department?.toLowerCase().includes(term)
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(req => req.status === statusFilter);
    }
    
    if (departmentFilter !== 'all') {
      filtered = filtered.filter(req => 
        (req.department || '').toLowerCase() === departmentFilter.toLowerCase()
      );
    }
    
    if (dateRange.start) {
      filtered = filtered.filter(req => new Date(req.created_at) >= new Date(dateRange.start));
    }
    if (dateRange.end) {
      const endDate = new Date(dateRange.end);
      endDate.setHours(23, 59, 59);
      filtered = filtered.filter(req => new Date(req.created_at) <= endDate);
    }
    
    if (hasRemarksFilter === 'has_remarks') {
      filtered = filtered.filter(req => req.has_auditor_remark);
    } else if (hasRemarksFilter === 'no_remarks') {
      filtered = filtered.filter(req => !req.has_auditor_remark);
    }
    
    setFilteredRequests(filtered);
  }, [purchaseRequests, searchTerm, statusFilter, departmentFilter, dateRange, hasRemarksFilter]);

  const uniqueDepartments = useMemo(() => {
    const depts = new Set();
    purchaseRequests.forEach(req => {
      if (req.department) depts.add(req.department);
    });
    return Array.from(depts).sort();
  }, [purchaseRequests]);

  // Statistics
  const auditStats = useMemo(() => {
    const total = purchaseRequests.length;
    const withRemarks = purchaseRequests.filter(r => r.has_auditor_remark).length;
    const withoutRemarks = total - withRemarks;
    const totalValue = purchaseRequests.reduce((sum, r) => sum + (r.total_amount || 0), 0);
    
    return {
      total,
      withRemarks,
      withoutRemarks,
      coverageRate: total > 0 ? (withRemarks / total) * 100 : 0,
      totalValue,
    };
  }, [purchaseRequests]);

  // ==================== INITIALIZATION ====================
  useEffect(() => {
    fetchUserProfile();
    fetchPurchaseRequests();
  }, [fetchUserProfile, fetchPurchaseRequests]);

  // ==================== RENDER COMPONENTS ====================
  const StatCard = ({ title, value, subtitle, color, icon: Icon }) => (
    <Card sx={{
      borderRadius: 3,
      height: '100%',
      boxShadow: '0px 2px 8px rgba(0,0,0,0.1)',
      transition: 'transform 0.2s ease-in-out',
      '&:hover': { transform: 'translateY(-3px)' },
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500, textTransform: 'uppercase' }}>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight="bold" sx={{ color, mt: 1 }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Avatar sx={{ bgcolor: alpha(color, 0.1), color, width: 56, height: 56 }}>
            <Icon sx={{ fontSize: 32 }} />
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  const RequestCard = ({ request }) => (
    <Card sx={{ 
      mb: 2, 
      borderRadius: 2,
      transition: 'all 0.2s ease',
      '&:hover': { boxShadow: theme.shadows[4] },
      borderLeft: `4px solid ${REQUEST_STATUSES[request.status]?.color || '#757575'}`,
      opacity: processingId === request.id ? 0.7 : 1,
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              {request.po_number}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {request.requested_by_name} • {request.department || 'Store Keeping'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {getStatusChip(request.status)}
            {request.has_auditor_remark && (
              <Chip 
                label="Audited" 
                size="small" 
                icon={<AuditIcon sx={{ fontSize: 14 }} />}
                sx={{ bgcolor: alpha('#9C27B0', 0.1), color: '#9C27B0' }}
              />
            )}
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="h6" color="#9C27B0" fontWeight="bold">
              {formatCurrency(request.total_amount)}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {request.total_items} item(s) • Created: {formatDate(request.created_at)}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="View Details">
              <IconButton size="small" onClick={() => {
                setSelectedRequest(request);
                setViewDialogOpen(true);
              }}>
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Add Auditor Remark">
              <IconButton 
                size="small" 
                sx={{ color: '#9C27B0' }} 
                onClick={() => {
                  setSelectedRequest(request);
                  setAuditorRemark('');
                  setRemarkDialogOpen(true);
                }}
              >
                <AddCommentIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Print Audit Report">
              <IconButton size="small" onClick={() => {
                setSelectedRequest(request);
                setTimeout(() => handlePrintPO(), 100);
              }}>
                <PrintIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        
        {request.auditor_remark?.length > 0 && (
          <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #eee' }}>
            <Typography variant="caption" color="textSecondary" display="flex" alignItems="center" gap={0.5}>
              <AuditIcon sx={{ fontSize: 12 }} />
              Latest audit remark: {request.auditor_remark[request.auditor_remark.length - 1]?.remark?.substring(0, 60)}...
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  // Loading state
  if (loading && purchaseRequests.length === 0) {
    return (
      <Box sx={{ display: 'flex', height: '100vh' }}>
        <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <Box sx={{ flexGrow: 1, p: 3, ml: isMobile ? 0 : sidebarOpen ? 30 : 5, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
          <CircularProgress size={60} sx={{ color: '#9C27B0' }} />
          <Typography sx={{ mt: 2, color: '#666' }}>Loading purchase orders for audit review...</Typography>
        </Box>
      </Box>
    );
  }

  // Check if user has auditor role
  if (!validateAuditorUser(userProfile.user_type) && userProfile.user_type !== '') {
    return (
      <Box sx={{ display: 'flex', height: '100vh' }}>
        <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <Box sx={{ flexGrow: 1, p: 3, ml: isMobile ? 0 : sidebarOpen ? 30 : 5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Alert severity="error" sx={{ maxWidth: 500 }}>
            <Typography variant="h6" gutterBottom>Access Denied</Typography>
            <Typography>You do not have permission to access this page. Auditor role is required.</Typography>
          </Alert>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#f5f5f5' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <Box sx={{
        flexGrow: 1,
        p: 3,
        ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
        maxWidth: '1400px',
        mx: 'auto',
        width: '100%',
        overflow: 'auto',
        height: '100vh',
      }}>
        <TopNav
          onLogout={() => {
            logSecurityEvent('AUDITOR_LOGOUT', {});
            localStorage.clear();
            window.location.href = '/login';
          }}
          onNotificationClick={() => console.debug('[AuditorRemark] Notification clicked')}
          toggleSidebar={toggleSidebar}
        />

        {/* Header Section */}
        <Paper elevation={0} sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)',
          color: 'white',
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h4" fontWeight="bold" sx={{ fontFamily: 'Times New Roman, serif' }}>
                Auditor Review Dashboard
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                Review and add audit remarks to purchase orders
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <Chip 
                  label={`Welcome, ${userProfile.name || 'Auditor'}`} 
                  size="small" 
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                />
              </Box>
            </Box>
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
              onClick={fetchPurchaseRequests}
              disabled={loading}
              sx={{ bgcolor: 'white', color: '#9C27B0', '&:hover': { bgcolor: '#f5f5f5' }, textTransform: 'none' }}
            >
              Refresh
            </Button>
          </Box>
        </Paper>

        {/* Statistics Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard 
              title="Total Purchase Orders" 
              value={auditStats.total} 
              color="#2196f3" 
              icon={ReceiptIcon}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard 
              title="Audited" 
              value={auditStats.withRemarks} 
              subtitle={`${auditStats.coverageRate.toFixed(1)}% coverage`}
              color="#4caf50" 
              icon={AuditIcon}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard 
              title="Pending Audit" 
              value={auditStats.withoutRemarks} 
              color="#ff9800" 
              icon={PendingIcon}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard 
              title="Total Value" 
              value={formatCurrency(auditStats.totalValue)} 
              color="#9C27B0" 
              icon={AccountBalanceIcon}
            />
          </Grid>
        </Grid>

        {/* Filters */}
        <Paper sx={{ borderRadius: 3, overflow: 'hidden', mb: 3 }}>
          <Box sx={{ p: 2, bgcolor: '#fafafa', borderBottom: 1, borderColor: 'divider' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search by PO number, requester..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="account_approved">Account Approved</MenuItem>
                    <MenuItem value="gm_approved">GM Approved</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Department</InputLabel>
                  <Select
                    value={departmentFilter}
                    onChange={(e) => setDepartmentFilter(e.target.value)}
                    label="Department"
                  >
                    <MenuItem value="all">All Departments</MenuItem>
                    {uniqueDepartments.map(dept => (
                      <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Audit Status</InputLabel>
                  <Select
                    value={hasRemarksFilter}
                    onChange={(e) => setHasRemarksFilter(e.target.value)}
                    label="Audit Status"
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="has_remarks">Has Auditor Remarks</MenuItem>
                    <MenuItem value="no_remarks">No Auditor Remarks</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={1.5}>
                <TextField
                  size="small"
                  type="date"
                  fullWidth
                  label="From"
                  value={dateRange.start}
                  onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={1.5}>
                <TextField
                  size="small"
                  type="date"
                  fullWidth
                  label="To"
                  value={dateRange.end}
                  onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </Box>
        </Paper>

        {/* Requests List */}
        <Box>
          {filteredRequests.length === 0 ? (
            <Paper sx={{ p: 5, textAlign: 'center', borderRadius: 3 }}>
              <AuditIcon sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
              <Typography variant="h6" color="textSecondary">
                No purchase orders found
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Adjust your filters or refresh the page
              </Typography>
            </Paper>
          ) : (
            filteredRequests.map(request => (
              <RequestCard key={request.id} request={request} />
            ))
          )}
        </Box>

        {/* Results Summary */}
        {filteredRequests.length > 0 && (
          <Paper sx={{ p: 2, mt: 2, borderRadius: 2, bgcolor: '#fafafa' }}>
            <Typography variant="body2">
              <strong>Showing:</strong> {filteredRequests.length} of {purchaseRequests.length} purchase order(s)
              {searchTerm && ` matching "${searchTerm}"`}
            </Typography>
          </Paper>
        )}

        <footer style={{ textAlign: 'center', padding: '20px', marginTop: 'auto' }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body2" color="textSecondary">
            Designed by Elegantè IT - Auditor Review Management System
          </Typography>
        </footer>
      </Box>

      {/* ==================== VIEW DETAILS DIALOG ==================== */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6">Purchase Order Details</Typography>
              <Typography variant="body2" color="textSecondary">{selectedRequest?.po_number}</Typography>
            </Box>
            <IconButton onClick={() => setViewDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Stack spacing={3} sx={{ mt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Order Information</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 1 }}>
                      <Typography variant="caption" color="textSecondary">PO Number:</Typography>
                      <Typography variant="body2">{selectedRequest.po_number}</Typography>
                      <Typography variant="caption" color="textSecondary">Requester:</Typography>
                      <Typography variant="body2">{selectedRequest.requested_by_name}</Typography>
                      <Typography variant="caption" color="textSecondary">Department:</Typography>
                      <Typography variant="body2">{selectedRequest.department || 'Store Keeping'}</Typography>
                      <Typography variant="caption" color="textSecondary">Created:</Typography>
                      <Typography variant="body2">{formatDateTime(selectedRequest.created_at)}</Typography>
                      <Typography variant="caption" color="textSecondary">Total Amount:</Typography>
                      <Typography variant="body2" fontWeight="bold" color="#9C27B0">{formatCurrency(selectedRequest.total_amount)}</Typography>
                    </Box>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Approval Status</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      {getStatusChip(selectedRequest.status)}
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 1 }}>
                      <Typography variant="caption" color="textSecondary">Account Approved:</Typography>
                      <Typography variant="body2">{selectedRequest.account_approved_by ? `${selectedRequest.account_approved_by} (${formatDate(selectedRequest.account_approved_at)})` : 'Not yet'}</Typography>
                      <Typography variant="caption" color="textSecondary">GM Approved:</Typography>
                      <Typography variant="body2">{selectedRequest.gm_approved_by ? `${selectedRequest.gm_approved_by} (${formatDate(selectedRequest.gm_approved_at)})` : 'Not yet'}</Typography>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>

              {/* Items Table */}
              <Typography variant="subtitle1" fontWeight="bold">Ordered Items</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell>S/N</TableCell>
                      <TableCell>Item</TableCell>
                      <TableCell>Specification</TableCell>
                      <TableCell align="right">Quantity</TableCell>
                      <TableCell>Unit</TableCell>
                      <TableCell align="right">Unit Price</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(selectedRequest.items || []).map((item, idx) => (
                      <TableRow key={item.id || idx}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>{sanitizeString(item.item_name)}</TableCell>
                        <TableCell>{sanitizeString(item.specification || '-')}</TableCell>
                        <TableCell align="right">{sanitizeNumber(item.quantity).toLocaleString()}</TableCell>
                        <TableCell>{sanitizeString(item.unit_of_measurement || 'pcs')}</TableCell>
                        <TableCell align="right">{formatCurrency(item.unit_price)}</TableCell>
                        <TableCell align="right">{formatCurrency(item.total_price)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Auditor Remarks History */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    <AuditIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
                    Auditor Remarks History
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddCommentIcon />}
                    onClick={() => {
                      setViewDialogOpen(false);
                      setRemarkDialogOpen(true);
                    }}
                    sx={{ borderColor: '#9C27B0', color: '#9C27B0' }}
                  >
                    Add Remark
                  </Button>
                </Box>
                
                {selectedRequest.auditor_remark?.length > 0 ? (
                  selectedRequest.auditor_remark.map((remark, idx) => (
                    <Paper key={idx} variant="outlined" sx={{ p: 1.5, mt: 1, bgcolor: alpha('#9C27B0', 0.05), borderLeft: `3px solid #9C27B0` }}>
                      <Typography variant="body2">
                        <strong>{remark.added_by}:</strong> {remark.remark}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" display="block">
                        {formatDateTime(remark.added_at)}
                      </Typography>
                    </Paper>
                  ))
                ) : (
                  <Typography variant="body2" color="textSecondary" sx={{ py: 2, textAlign: 'center' }}>
                    No auditor remarks have been added to this purchase order yet.
                  </Typography>
                )}
              </Paper>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          <Button 
            variant="contained" 
            startIcon={<PrintIcon />}
            onClick={handlePrintPO}
            sx={{ bgcolor: '#9C27B0', '&:hover': { bgcolor: '#7B1FA2' } }}
          >
            Print Audit Report
          </Button>
        </DialogActions>
      </Dialog>

      {/* ==================== ADD REMARK DIALOG ==================== */}
      <Dialog open={remarkDialogOpen} onClose={() => setRemarkDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#9C27B0', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AuditIcon /> Add Auditor Remark
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Alert severity="info" icon={<InfoIcon />}>
              <strong>PO Number:</strong> {selectedRequest?.po_number}<br />
              <strong>Requester:</strong> {selectedRequest?.requested_by_name}<br />
              <strong>Total Amount:</strong> {formatCurrency(selectedRequest?.total_amount)}
            </Alert>
            
            <Alert severity="warning">
              <strong>📋 Auditor Note:</strong> Your remarks will be permanently recorded for audit trail and compliance purposes.
            </Alert>
            
            <TextField
              label="Auditor Remark *"
              multiline
              rows={5}
              fullWidth
              required
              value={auditorRemark}
              onChange={(e) => setAuditorRemark(e.target.value)}
              placeholder="Enter your audit observation, compliance note, or recommendation here..."
              error={auditorRemark && auditorRemark.length < 3}
              helperText={auditorRemark && auditorRemark.length < 3 ? "Please provide at least 3 characters" : "Required - Detailed audit remark"}
              autoFocus
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setRemarkDialogOpen(false);
            setAuditorRemark('');
          }}>
            Cancel
          </Button>
          <Button 
            onClick={handleAddRemark} 
            variant="contained" 
            sx={{ bgcolor: '#9C27B0', '&:hover': { bgcolor: '#7B1FA2' } }}
            disabled={loading || !auditorRemark || auditorRemark.trim().length < 3}
          >
            {loading && processingId === selectedRequest?.id ? <CircularProgress size={24} /> : 'Submit Remark'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AuditorRemark;